import { Component, OnInit } from '@angular/core';
import { StepManager } from '../../../../shared/IFE/step/step-manager/step-manager';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AnalyticsService } from '../../../../shared/modules/analytics/services/analytics.service';
import { QuoteSteps, quoteStepsOrder } from '../../models/quote-steps';
import { Simulation } from '../../../../shared/modules/evc-api/models/simulation';
import { EvcApiService } from '../../../../shared/modules/evc-api/services/evc-api.service';
import { Quote } from '../../../../shared/modules/evc-api/models/quote';
import { ProgressBarType } from 'src/app/shared/IFE/components/progress-bar/models/progress-bar-type';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, finalize, first, skip, take, takeUntil } from 'rxjs/operators';
import { QuoteHelperService } from '../../services/quote-helper.service';
import { CarValues, MetaSteps } from '../../models/constants';
import { ToastrService } from 'ngx-toastr';
import { UtilsService } from '../../../../shared/utils/utils.service';
import { Status } from '../../../../shared/modules/evc-api/models/constants';
import { environment } from '../../../../../environments/environment';
import { PartnerService } from '../../services/partner/partner.service';
import { PartnerUI } from '@app/shared/modules/evc-api/models/partner-ui.interface';
import { RouteName, routeMap } from 'src/app/app.settings';

@Component({
  selector: 'app-evc-quote',
  templateUrl: './evc-quote.component.html',
  styleUrls: ['./evc-quote.component.scss'],
})
export class EvcQuoteComponent extends StepManager implements OnInit {
  readonly quoteSteps = QuoteSteps;
  readonly ProgressBarType = ProgressBarType;

  public quote: Quote;
  public control: UntypedFormGroup;

  public isCompleted$: BehaviorSubject<boolean> = new BehaviorSubject(false); //activeStep === quoteSteps.confirmation;

  public quoteStepsOrder = quoteStepsOrder;

  public isLoading = false;

  public activeSubStep: string;
  private simulation: Simulation;
  private standalone: boolean;

  private partner: PartnerUI;
  private quote$: Observable<Quote>;

  constructor(
    location: Location,
    router: Router,
    title: Title,
    translate: TranslateService,
    private fb: UntypedFormBuilder,
    private analyticsService: AnalyticsService,
    private api: EvcApiService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private partnerService: PartnerService
  ) {
    super(location, router, title, translate);

    this.partnerService.restoredPartner$.subscribe((partner: PartnerUI) => {
      if (partner) {
        this.partner = partner;
      }
    });
  }

  ngOnInit(): void {
    const quoteRegex = routeMap.find((el) => el.name === RouteName.quote).regex;
    this.standalone = !quoteRegex.test(window.location.pathname);

    this.buildForm();
    // Set the title string.
    this.setTitle('modules.evcQuote.general.title');

    // Check if we have a Quote id in url and retrieve the data.
    if (this.route.snapshot.queryParams.id) {
      this.quote$ = this.api.getQuote(this.route.snapshot.queryParams.id);

      // If this is the first Time we receive a valid quote. We set the initial values
      this.quote$.pipe(first((value) => value !== null)).subscribe(
        (quote: Quote) => {
          // If we receive a quote ID that is already done, we redirect to the thank you page.
          if (quote.status === Status.done) {
            // Don't create one string literal on one row (redirecting URL doesn't work properly after formatting)
            const lang = this.translate.currentLang.replace(/\b\w/g, (f) => f.toLocaleUpperCase());

            window.location.href = `${environment.websiteUrl}${environment[`thankYouPage${lang}`]}`;
          }

          const stepDetails = quote.step.split('.');
          if (stepDetails[1]) {
            this.activeSubStep = stepDetails[1];
          }

          this.control.patchValue(quote.quote, { onlySelf: true });

          // we need the initial active step from the url for the path rewrite.
          this.activeStep = this.router.url.split('/').pop().replace(/\?.*/g, '');
          this.goToStep(stepDetails[0]);
          this.isCompleted$.next(this.activeStep === this.quoteSteps.confirmation);
        },
        (error) => {
          this.toastr.error(this.translate.instant('errorMessages.serverError'));
          throw error;
        }
      );

      // Everytime a quote is updated we update our values.
      this.quote$
        .pipe(
          filter((value) => value !== null),
          takeUntil(this.notifier)
        )
        .subscribe(
          (value) => {
            this.quote = value;
          },
          (error) => {
            this.toastr.error(this.translate.instant('errorMessages.serverError'));
            throw error;
          }
        );
    }

    // Check if we have a SimId id in url and retrieve the data.
    if (this.route.snapshot.queryParams.simId) {
      this.api
        .getSimulation(this.route.snapshot.queryParams.simId)
        .pipe(first((value) => value !== null))
        .subscribe(
          (value: Simulation) => {
            this.simulation = value;
            this.goToStep(QuoteSteps.intro);
          },
          (error) => {
            this.toastr.error(this.translate.instant('errorMessages.serverError'));
            throw error;
          }
        );
    }

    // No Sim Id or Quote Id, go the the simulator.
    if (!this.route.snapshot.queryParams.id && !this.route.snapshot.queryParams.simId) {
      this.router.navigate(['/', this.translate.currentLang, 'evc-segment'], { queryParamsHandling: 'merge' });
    }

    this.analytics();
  }

  getSimulationUrl() {
    return this.standalone
      ? `/${this.translate.currentLang}/evc-quote` + window.location.search
      : routeMap.find((el) => el.name === RouteName.quote).basePath[this.translate.currentLang] +
          window.location.search;
  }

  startQuote() {
    // Convert the Simulation in to a Quote
    const newQuote = QuoteHelperService.convertSimulationToQuote(
      this.simulation,
      this.partner,
      this.translate.currentLang
    );

    this.api
      .saveQuote(newQuote)
      .pipe(take(1))
      .subscribe(
        (value) => {
          // Remove the simId from the param list.
          const params = { ...this.route.snapshot.queryParams };
          delete params.simId;
          params.id = value.id;
          this.router.navigate([`../${QuoteSteps.carDetails}`], {
            queryParams: params,
            relativeTo: this.route,
          });
        },
        (error) => {
          this.toastr.error(this.translate.instant('errorMessages.serverError'));
          throw error;
        }
      );
  }

  submit() {
    this.quote.status = Status.completed;
    if (this.control.get('newsletters').value) {
      // Hardcoded newsletter code for EV Mailing list.
      this.quote.newsletters = ['MKT_EV'];
    }
    const timer = setTimeout(() => {
      this.isLoading = true;
    }, 300);

    // Send quote conversion is finished
    this.analyticsService.push({
      event: 'evcQuote-Completed',
      page: {
        quoteConversion: 1,
      },
    });

    this.save()
      .pipe(
        skip(1),
        take(1),
        finalize(() => {
          clearTimeout(timer);
          this.isLoading = false;
        })
      )
      .subscribe(
        () => {
          // Redirect to the thank you page
          // Don't create one string literal on one row (redirecting URL doesn't work properly after formatting)
          const lang = this.translate.currentLang.replace(/\b\w/g, (f) => f.toLocaleUpperCase());
          let url = `${environment.websiteUrl}${environment[`thankYouPage${lang}`]}`;
          url += `${this.partner ? `?partner=${this.partner.name}` : ''}`;
          url += `${this.partner && this.partner.resellerCode ? `&resellerCode=${this.partner.resellerCode}` : ''}`;

          window.location.href = url;
        },
        () => {
          this.toastr.error(this.translate.instant('errorMessages.serverError'));
        }
      );
  }

  // get next step after Charging Station Details step if partner Kia param included
  getChargingStationDetailsNextStep(): string {
    return this.partner && (this.partner.name || '').toLocaleLowerCase() === CarValues.kia
      ? this.quoteSteps.personalDetails
      : this.quoteSteps.technicalDetails;
  }

  // get back step before Personal Details step if partner Kia param included
  getPersonalDetailsBackStep(): string {
    return this.partner && (this.partner.name || '').toLocaleLowerCase() === CarValues.kia
      ? this.quoteSteps.chargingStationDetails
      : this.quoteSteps.houseDetails;
  }

  protected saveAndGoToStep(step: string = null, back: boolean = false) {
    this.activeSubStep = back ? MetaSteps.last : MetaSteps.first;
    super.goToStep(step);

    this.isCompleted$.next(step === this.quoteSteps.confirmation);

    this.quote.status = Status.inProgress;
    this.save();
  }

  protected save() {
    this.quote.step = this.activeSubStep ? `${this.activeStep}.${this.activeSubStep}` : this.activeStep;
    const current = this.quoteStepsOrder.findIndex((s) => s.step === this.activeStep);
    const highestReached = this.quoteStepsOrder.findIndex((s) => s.step === this.quote.stepReached);

    this.quote.stepReached = current > highestReached ? this.activeStep : this.quote.stepReached;
    this.quote.quote = this.control.value;
    this.quote.lang = this.translate.currentLang;

    if (this.partner) {
      this.quote.quote.partnerDetails = {
        ...(this.partner.name ? { partner: this.partner.name } : {}),
        ...(this.partner.resellerCode ? { resellerCode: this.partner.resellerCode } : {}),
      };
    }

    this.valueAnalytics(this.control.value);
    return this.api.updateQuote(this.quote.id, this.quote);
  }

  protected stepNavigation(subStep: string) {
    this.activeSubStep = subStep;
    this.save();
  }

  private buildForm() {
    this.control = this.fb.group({
      carDetails: [],
      chargingStationDetails: [],
      technicalDetails: [],
      houseDetails: [],
      personalDetails: [],
      newsletters: [],
    });
  }

  private analytics() {
    this.analyticsService.push({
      event: 'evcQuote-Pageview',
      page: {
        phase: 'sales',
        category: 'evc-quote',
        branch: 'charging-station',
        simType: 'full',
        quoteConversion: 0,
        language: this.translate.currentLang,
      },
    });
  }

  private valueAnalytics(values) {
    //Deep Clone the values.
    const stringValues = JSON.stringify(values);
    const analyticsValues = JSON.parse(stringValues);

    UtilsService.prune(analyticsValues);

    // We don't need to send some values to GA
    delete analyticsValues.technicalDetails?.fuseBox;
    delete analyticsValues.technicalDetails?.placement;
    delete analyticsValues.technicalDetails?.mainFuse;
    delete analyticsValues.technicalDetails?.trajectory;

    // We send no personal details to GA
    delete analyticsValues.personalDetails?.contactDetails;

    this.analyticsService.push({
      event: 'evcQuote-Interaction',
      eventAction: 'valueChange',
      page: {
        evcQuote: analyticsValues,
      },
    });
  }
}
